import { useEffect, useState } from "react"

import { useLazyGetMobileUsageWidgetQuery } from "store/api/adminApi"
import { useLazyGetMobileUsageWidgetQuery as useLazyGetClientMobileUsageWidgetQuery } from "store/api/clientApi"

import { getDateRangeFromOption } from "utils/dateUtils"
import { HalfDoughnutSummaryWidget } from "components/widgets/HalfDoughnutSummaryWidget"
import { allDateSelectOptions } from "pages/vendor/Dashboard/constants"

const CHART_COLORS = {
  withMobileApp: "#22C55E",
  withoutMobileApp: "#E2E8F0",
} as const

interface Props {
  isVendor?: boolean
}

export const WidgetMobileAppUsage = ({ isVendor = false }: Props) => {
  const [selectedDateRange, setSelectedDateRange] = useState(() => getDateRangeFromOption("all"))
  const [triggerVendorMobileUsage, { isFetching: isVendorFetching, data: vendorData }] =
    useLazyGetMobileUsageWidgetQuery()
  const [triggerClientMobileUsage, { isFetching: isClientFetching, data: clientData }] =
    useLazyGetClientMobileUsageWidgetQuery()
  const [widgetData, setWidgetData] = useState({ withMobileApp: 0, withoutMobileApp: 0 })

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isVendor) {
          await triggerVendorMobileUsage()
        } else {
          const [dateStart, dateEnd] = selectedDateRange
          await triggerClientMobileUsage(
            dateStart && dateEnd
              ? ({ date_start: dateStart, date_end: dateEnd } as Parameters<typeof triggerClientMobileUsage>[0])
              : undefined,
          )
        }
      } catch (error) {
        console.error(error)
      }
    }

    fetchData()
  }, [isVendor, selectedDateRange, triggerVendorMobileUsage, triggerClientMobileUsage])

  useEffect(() => {
    const d = isVendor
      ? ((
          vendorData as
            | { data?: { mobile_app_activated: number; mobile_app_not_activated?: number; customers: number } }
            | undefined
        )?.data ?? vendorData)
      : clientData?.data
    if (!d) return
    setWidgetData({
      withMobileApp: d.mobile_app_activated ?? 0,
      withoutMobileApp: d.mobile_app_not_activated ?? (d.customers ?? 0) - (d.mobile_app_activated ?? 0),
    })
  }, [isVendor, vendorData, clientData])

  const items = [
    { label: "With Mobile app", color: CHART_COLORS.withMobileApp, value: widgetData.withMobileApp },
    {
      label: "Without Mobile app",
      color: CHART_COLORS.withoutMobileApp,
      value: widgetData.withoutMobileApp,
    },
  ]

  return (
    <HalfDoughnutSummaryWidget
      title="Mobile app Usage"
      mainLabel="Customers"
      items={items}
      chartOptions={{ cutout: "65%" }}
      showPercentage
      isFetching={isVendor ? isVendorFetching : isClientFetching}
      dateSelectOptions={!isVendor ? allDateSelectOptions : undefined}
      onSelectDateRange={!isVendor ? setSelectedDateRange : undefined}
      defaultSelectedDateOption={!isVendor ? allDateSelectOptions[0] : undefined}
    />
  )
}
